import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Alert, Box, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import logo from '../../../assets/img/brand/logo-digit-h.png';
import img_login from '../../../assets/img/login/login.png';
import AuthContext from '../../../AuthContext';
import Loading from '../../../components/Loading/Loading';
import auth from '../../../services/Auth';
import { DigitSsoButton } from './DigitSsoButton';
import { ErrorToast } from './ErrorToast';

const DIGIT_SSO_LINK = '' + process.env.REACT_APP_AUTH_BASE_ENDPOINT + process.env.REACT_APP_AUTH_DIGIT_SSO_PATH;

const MainContainer = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
});

const CustomBox = styled(Box)({
  minHeight: '100vh',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const ImgBox = styled(Box)({
  maxWidth: '100%',
});

function Login(props) {
  const [accessToken, refreshToken] = useMemo(() => {
    const searchParams = new URLSearchParams(props.location.search);

    return [searchParams.get('token'), searchParams.get('refresh_token')];
  }, [props.location.search]);

  const authContext = useContext(AuthContext);
  const [isAuthenticating, setAuthenticating] = useState(false);
  const theme = useTheme();
  const matchWidth = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    console.log('Login - Effect with :', accessToken, refreshToken, authContext);

    if (isAuthenticating) {
      console.log('Login - Authenticated in progress, waiting');

      return;
    }

    if (
      !accessToken &&
      !authContext.isAuthenticated &&
      !auth.validateTokenFromLocalstorage({ logoutIfNoUserFound: false })
    ) {
      console.log('Login - No authentication to perform');

      return;
    }

    // Handle auth when accessToken refreshed
    if (!accessToken && authContext.isAuthenticated && !auth.isAuthenticated()) {
      authContext.setAuthenticated(false);

      return;
    }

    setAuthenticating(true);

    if (!accessToken && authContext.isAuthenticated) {
      console.log('Login - redirect');
      setAuthenticating(false);
      auth.redirectAfterAuthentication();

      return;
    }

    if (auth.hasNextUrl() && props.match.url === auth.getNextUrl()) {
      console.log('Login - reset');
      auth.resetNextUrl();
    }

    if (accessToken && !authContext.isAuthenticated) {
      console.log('Login - Authenticating from token passed in url');
      //Authenticating from token passed in url
      auth
        .handleToken({ accessToken, refreshToken })
        .then(isAuthenticated => {
          console.log('Login - Authenticated: ', isAuthenticated);

          // Set Authentication status
          authContext.setAuthenticated(isAuthenticated);
          setAuthenticating(false);

          if (!isAuthenticated) {
            console.error('Login - Error while authenticating user.');

            return;
          }

          if (auth.hasRedirectPathInToken()) {
            auth.storeNextUrl(auth.getRedirectPathInToken());
          }

          // Then redirect to nextURL or home
          auth.redirectAfterAuthentication();
        })
        .catch(e => {
          console.error('Login - Error while authenticating user.', e);

          // Reset Authentication status
          authContext.setAuthenticated(false);
          setAuthenticating(false);
        });
    } else if (!authContext.isAuthenticated) {
      // Try to authenticate from local storage
      console.log('Login - Authenticating user from local storage.');

      auth
        .handleAuthentication({ logoutIfNoUserFound: true })
        .then(isAuthenticated => {
          console.log('Login - Authenticated: ', isAuthenticated);
          authContext.setAuthenticated(isAuthenticated);
          setAuthenticating(false);
          if (isAuthenticated) {
            // Redirect to home
            auth.redirectAfterAuthentication();
          }
        })
        .catch(e => {
          console.error('Login - Error while authenticating user from local storage.', e);
          authContext.setAuthenticated(false);
          setAuthenticating(false);
        });
    } else {
      console.log('Login - No authentication to perfom');
    }
  }, [authContext, isAuthenticating, accessToken, refreshToken, props.match.url]);

  const error_message = props.match.params.error_message;

  return (
    <MainContainer id='page_login' sx={{ backgroundColor: 'extraLightGray' }}>
      {isAuthenticating && <Loading type={Loading.TYPE.DIGIT_FULLSCREEN} />}
      {matchWidth && (
        <CustomBox px={2} sx={{ width: '58%' }}>
          <ImgBox component='img' alt='Login' src={img_login} mb={4} />
        </CustomBox>
      )}
      <CustomBox sx={{ width: matchWidth ? '42%' : '100%', backgroundColor: 'primary.light' }}>
        <CustomBox sx={{ flexDirection: 'column' }}>
          <ImgBox component='img' alt='Logo' src={logo} sx={{ height: '80px', marginBottom: '60px' }} />
          <Box mb={error_message ? 1 : 5}>
            <DigitSsoButton size={18} digitSsoLink={DIGIT_SSO_LINK} />
          </Box>
          {error_message && (
            <>
              <Box mt={4} sx={{ width: '100%' }}>
                <Alert icon={false} severity='error'>
                  {decodeURIComponent(error_message)}
                </Alert>
              </Box>

              <ErrorToast message={error_message} showIcon={false} />
            </>
          )}
        </CustomBox>
      </CustomBox>
    </MainContainer>
  );
}

export default Login;
